import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import {
  FaUser,
  FaEnvelope,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaMoneyBillWave,
  FaSeedling,
  FaMapMarkerAlt,
  FaChevronDown,
} from "react-icons/fa";
import "./styles.css";
import logoImage from "../../assets/SignUp/logo.jpeg";
import CurrencyList from "currency-list";
import { auth, createUserWithEmailAndPassword } from "../../index";
import axios from "axios";

const SignUp = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // State for all form fields
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    accountCurrency: "",
    farmName: "",
    farmAddress: "",
    farmType: "soil", // 'soil' or 'aquatic'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    try {
      //  Step 1: Create the user in Firebase Authentication
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      const user = userCredential.user;
      const firebaseId = user.uid;
      const token = await user.getIdToken();

      // Step 2: Prepare the data payload for your backend API
      // FCM token registration will be handled by NotificationContext after signup
      const requestBody = {
        email: formData.email,
        firebaseId: firebaseId,
        username: formData.username,
        farmName: formData.farmName,
        farmLocation: formData.farmAddress || null,
        farmType: formData.farmType === "aquatic" ? "hydroponic" : "soil",
        currency: formData.accountCurrency || null
      };

      // Step 3: Call your backend to register the new tenant and user
      await axios.post("/api/auth/newSignup", requestBody, {
        headers: {
          Authorization: token,
        },
      });
      // Step 4: Navigate to phone verfication page
      navigate("/verify-phone", { state: { firebaseId: firebaseId } });
    } catch (error) {
      if (error.code) {
        if (error.code === "auth/email-already-in-use") {
          setError(t("This email address is already in use."));
        } else if (error.code === "auth/weak-password") {
          setError(t("The password must be at least 6 characters long."));
        } else {
          setError(t("An authentication error occurred."));
        }
      } else if (error.response) {
        setError(
          t("A server error occurred during registration. Please try again.")
        );
      } else {
        setError(
          t("An unexpected error occurred. Please check your connection.")
        );
      }
      console.error("Sign up error:", error);
    }
  };

  const currencyOptions = useMemo(() => {
    const currenciesAsObject = CurrencyList.getAll(i18n.language);
    // This converts the object into an ARRAY, which fixes the error
    return Object.values(currenciesAsObject);
  }, [i18n.language]);

  return (
    <div className="auth-page-container">
      <img src={logoImage} alt="Egro Care Logo" className="auth-logo" />
      <div
        className={classNames("auth-container", {
          rtl: i18n.language === "ar",
        })}
      >
        <h1 className="auth-header">{t("Create a new account")}</h1>

        <form onSubmit={handleSignUp} className="auth-form">
          {/* --- Input Fields --- */}
          <div className="input-wrapper">
            <FaUser className="input-icon" />
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder={t("Username")}
              className="signup-input-field"
              required
            />
          </div>

          <div className="input-wrapper">
            <FaEnvelope className="input-icon" />
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t("Email")}
              className="signup-input-field"
              required
            />
          </div>

          <div className="input-wrapper">
            <FaLock className="input-icon" />
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={t("Password")}
              className="signup-input-field"
              required
            />
            <span
              onClick={() => setShowPassword(!showPassword)}
              className="password-icon"
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
          </div>

          <div className="input-wrapper">
            <FaMoneyBillWave className="input-icon" />
            <select
              name="accountCurrency"
              value={formData.accountCurrency}
              onChange={handleChange}
              className={classNames("signup-selector", {
                "signup-placeholder": !formData.accountCurrency, // Apply 'placeholder' class if accountCurrency is empty
              })}
            >
              <option value="" disabled hidden>
                {t("account currency")}
              </option>
              {currencyOptions.map((currency) => (
                <option key={currency.code} value={currency.code}>
                  {currency.name} ({currency.code})
                </option>
              ))}
            </select>
            <FaChevronDown className="select-arrow-icon" />
          </div>

          <div className="input-wrapper">
            <FaSeedling className="input-icon" />
            <input
              type="text"
              name="farmName"
              value={formData.farmName}
              onChange={handleChange}
              placeholder={t("Farm Name")}
              className="signup-input-field"
              required
            />
          </div>

          <div className="input-wrapper">
            <FaMapMarkerAlt className="input-icon" />
            <input
              type="text"
              name="farmAddress"
              value={formData.farmAddress}
              onChange={handleChange}
              placeholder={t("Farm Address")}
              className="signup-input-field"
            />
          </div>

          {/* --- Farm Type Selector --- */}
          <label className="farm-type-label">{t("Farm Type")}</label>
          <div className="farm-type-container">
            <button
              type="button"
              className={classNames("farm-type-btn", {
                active: formData.farmType === "soil",
              })}
              onClick={() => setFormData({ ...formData, farmType: "soil" })}
            >
              {t("Soil-based")}
            </button>
            <button
              type="button"
              className={classNames("farm-type-btn", {
                active: formData.farmType === "aquatic",
              })}
              onClick={() => setFormData({ ...formData, farmType: "aquatic" })}
            >
              {t("Aquatic")}
            </button>
          </div>

          {error && <div className="error-message">{error}</div>}
          {successMessage && (
            <div className="success-message">{successMessage}</div>
          )}

          <button className="auth-btn" type="submit">
            {t("Create a new account")}
          </button>

          <p className="auth-link-text">
            {t("Do you already have an account?")}{" "}
            <Link to="/login">{t("Log in")}</Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default SignUp;
